import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

const TemplateVariableHandler = ({ 
  template, 
  initialVariables = {}, 
  onVariablesChange, 
  showPreview = true,
  disabled = false,
  className = ""
}) => {
  const [templateVariables, setTemplateVariables] = useState(initialVariables);
  const [messagePreview, setMessagePreview] = useState('');

  // Utility functions
  const extractVariables = (content) => {
    if (!content) return [];
    const matches = content.match(/\{\{(\d+)\}\}/g);
    return matches ? [...new Set(matches)] : [];
  };

  // Extract all variables from template (content + headerText)
  const extractAllTemplateVariables = (template) => {
    if (!template) return [];
    
    const variables = new Set();
    
    // Extract from content
    if (template.content) {
      const contentVars = extractVariables(template.content);
      contentVars.forEach(v => variables.add(v));
    }
    
    // Extract from headerText
    if (template.headerText) {
      const headerVars = extractVariables(template.headerText);
      headerVars.forEach(v => variables.add(v));
    }
    
    // Sort by number inside braces
    return Array.from(variables).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''));
      const numB = parseInt(b.replace(/\D/g, ''));
      return numA - numB;
    });
  };

  const generatePreview = (template, variables) => {
    if (!template || !template.content) return '';
    
    let preview = template.content;
    if (template.headerText) {
      preview = `${template.headerText}\n\n${preview}`;
    }
    
    // Replace variables with their values
    Object.keys(variables).forEach(expression => {
      const variable = variables[expression];
      if (variable && variable.value) {
        preview = preview.replace(new RegExp(expression.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
      }
    });
    
    return preview;
  };

  const hasVariables = (template) => {
    if (!template) return false;
    const bodyVariables = template.bodyVariables || [];
    const headerVariables = template.headerVariables || [];
    
    // Fallback: extract variables from content if backend variables are not available
    const hasRegexVariables = template.content && template.content.includes('{{') && template.content.includes('}}');
    const hasHeaderVariables = template.headerText && template.headerText.includes('{{') && template.headerText.includes('}}');
    
    const hasBackendVariables = bodyVariables.length > 0 || headerVariables.length > 0;
    const hasContentVariables = hasRegexVariables || hasHeaderVariables;
    
    return hasBackendVariables || hasContentVariables;
  };

  const handleVariableChange = (expression, type, value) => {
    const updatedVariables = {
      ...templateVariables,
      [expression]: { type, value }
    };
    
    setTemplateVariables(updatedVariables);
    
    // Update preview
    if (template) {
      const preview = generatePreview(template, updatedVariables);
      setMessagePreview(preview);
    }
    
    // Notify parent component
    if (onVariablesChange) {
      onVariablesChange(updatedVariables);
    }
  };

  const getVariableStatus = () => {
    if (!template || !hasVariables(template)) return null;
    
    // Use pre-extracted variables from backend
    let bodyVariables = template.bodyVariables || [];
    let headerVariables = template.headerVariables || [];
    
    // Fallback: extract variables from content if backend variables are not available
    if (bodyVariables.length === 0 && template.content) {
      bodyVariables = extractVariables(template.content);
    }
    if (headerVariables.length === 0 && template.headerText) {
      headerVariables = extractVariables(template.headerText);
    }
    
    const allVariables = [...bodyVariables, ...headerVariables];
    
    const hasAllValues = allVariables.every(expression => 
      templateVariables[expression] && templateVariables[expression].value && templateVariables[expression].value.trim()
    );
    
    return hasAllValues ? 'complete' : 'incomplete';
  };

  // Update preview when template changes
  useEffect(() => {
    if (template) {
      const preview = generatePreview(template, templateVariables);
      setMessagePreview(preview);
    }
  }, [template, templateVariables]);

  // Update variables when initialVariables change
  useEffect(() => {
    setTemplateVariables(initialVariables);
  }, [initialVariables]);

  if (!template) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 text-center ${className}`}>
        <div className="text-gray-500 text-sm">No template selected</div>
      </div>
    );
  }

  if (!hasVariables(template)) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              Template: {template.templateName}
            </h4>
            <p className="text-sm text-blue-700 mb-2">
              This template has no dynamic variables.
            </p>
            <div className="text-xs text-blue-600">
              <p>• Template will be sent as-is without variable substitution</p>
              <p>• No additional configuration is needed</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use pre-extracted variables from backend if available
  let bodyVariables = template.bodyVariables || [];
  let headerVariables = template.headerVariables || [];
  
  // Fallback: extract variables from content and headerText if backend variables are not available
  if (bodyVariables.length === 0 && headerVariables.length === 0) {
    // Extract all variables from both content and headerText
    const allExtracted = extractAllTemplateVariables(template);
    bodyVariables = allExtracted.filter(v => {
      // Check if variable appears in content
      return template.content && template.content.includes(v);
    });
    headerVariables = allExtracted.filter(v => {
      // Check if variable appears in headerText
      return template.headerText && template.headerText.includes(v);
    });
  } else {
    // If backend provides some variables, still extract to ensure we have all
    const allExtracted = extractAllTemplateVariables(template);
    if (bodyVariables.length === 0 && template.content) {
      bodyVariables = allExtracted.filter(v => template.content.includes(v));
    }
    if (headerVariables.length === 0 && template.headerText) {
      headerVariables = allExtracted.filter(v => template.headerText.includes(v));
    }
  }
  
  const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
  const status = getVariableStatus();

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Template Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="font-medium text-sm text-gray-900">{template.templateName}</div>
        <div className="text-xs text-gray-500 mt-1">{template.templateCategory}</div>
        <div className="text-xs text-gray-600 mt-2">
          Status: <span className={`px-2 py-1 rounded text-xs ${
            status === 'complete' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {status === 'complete' ? 'Complete' : 'Incomplete'}
          </span>
        </div>
      </div>

      {/* Variable Input Fields */}
      <div className="space-y-3">
        {allVariables.map(expression => {
          const variable = templateVariables[expression] || { type: 'TEXT', value: '' };
          return (
            <div key={expression} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{expression}</span>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`${expression}-type`}
                      value="TEXT"
                      checked={variable.type === 'TEXT'}
                      onChange={(e) => handleVariableChange(expression, e.target.value, variable.value)}
                      disabled={disabled}
                      className="mr-1 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-gray-600">TEXT</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`${expression}-type`}
                      value="PARAMS"
                      checked={variable.type === 'PARAMS'}
                      onChange={(e) => handleVariableChange(expression, e.target.value, 'contact_name')}
                      disabled={disabled}
                      className="mr-1 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-gray-600">PARAMS</span>
                  </label>
                </div>
              </div>
              
              {variable.type === 'TEXT' ? (
                <input
                  type="text"
                  value={variable.value}
                  onChange={(e) => handleVariableChange(expression, 'TEXT', e.target.value)}
                  placeholder={`Enter value for ${expression}`}
                  disabled={disabled}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              ) : (
                <select
                  value={variable.value}
                  onChange={(e) => handleVariableChange(expression, 'PARAMS', e.target.value)}
                  disabled={disabled}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="contact_name">contact_name</option>
                </select>
              )}
            </div>
          );
        })}
      </div>

      {/* Message Preview */}
      {showPreview && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center mb-2">
            <AlertCircle className="h-4 w-4 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-900">Message Preview</span>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-800 whitespace-pre-wrap">
              {messagePreview || generatePreview(template, templateVariables)}
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          Template Variables Help
        </h4>
        <ul className="text-xs text-gray-700 space-y-1">
          <li>• <strong>TEXT:</strong> Enter custom values for variables</li>
          <li>• <strong>PARAMS:</strong> Use dynamic contact data</li>
          <li>• Variables must be filled before saving</li>
          <li>• Preview updates in real-time as you type</li>
        </ul>
      </div>
    </div>
  );
};

export default TemplateVariableHandler;
